import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import './PractitionerAccessDialog.css';

function PractitionerAccessDialog() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [permissions, setPermissions] = useState({
    view_statistics: false,
    view_journal: false,
    view_mood_data: false,
    recommend_frequencies: false,
    write_notes: false
  });

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const data = await apiClient.getPendingAccessRequests();
      setRequests(data);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  };

  const handleRespond = async (requestId, approved) => {
    try {
      setLoading(true);
      await apiClient.respondToAccessRequest(requestId, approved, permissions);
      
      // Reload requests
      await loadRequests();
      setRespondingTo(null);
      setPermissions({
        view_statistics: false,
        view_journal: false,
        view_mood_data: false,
        recommend_frequencies: false,
        write_notes: false
      });
    } catch (error) {
      console.error('Error responding:', error);
      alert('Fehler beim Beantworten der Anfrage');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (key) => {
    setPermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (requests.length === 0) {
    return null;
  }

  return (
    <div className="access-requests-overlay">
      <div className="access-requests-container">
        <div className="access-requests-header">
          <h2>🔔 Zugriffsanfragen</h2>
          <p className="access-requests-subtitle">
            {requests.length} {requests.length === 1 ? 'Therapeut möchte' : 'Therapeuten möchten'} dich als Begleitung hinzufügen
          </p>
        </div>

        <div className="access-requests-list">
          {requests.map((request) => (
            <div key={request.id} className="access-request-card" data-testid="access-request-card">
              <div className="request-info">
                <div className="request-practitioner">
                  <span className="practitioner-icon">🩺</span>
                  <span className="practitioner-name">{request.practitioner_name}</span>
                </div>
                <p className="request-message">
                  möchte dich als Begleitung hinzufügen und deinen Heilungsweg unterstützen.
                </p>
              </div>

              {respondingTo === request.id ? (
                <div className="permissions-selector">
                  <h3 className="permissions-title">Berechtigungen wählen:</h3>
                  <p className="permissions-hint">Wähle, welche Daten geteilt werden sollen:</p>

                  <div className="permission-item">
                    <label className="permission-label">
                      <input
                        type="checkbox"
                        checked={permissions.view_statistics}
                        onChange={() => togglePermission('view_statistics')}
                        data-testid="permission-statistics"
                      />
                      <div className="permission-info">
                        <span className="permission-name">📊 Statistiken ansehen</span>
                        <span className="permission-desc">Aktivierungen, Häufigkeiten, Nutzungsmuster</span>
                      </div>
                    </label>
                  </div>

                  <div className="permission-item">
                    <label className="permission-label">
                      <input
                        type="checkbox"
                        checked={permissions.view_journal}
                        onChange={() => togglePermission('view_journal')}
                        data-testid="permission-journal"
                      />
                      <div className="permission-info">
                        <span className="permission-name">📖 Journal lesen</span>
                        <span className="permission-desc">Deine persönlichen Notizen und Erfahrungen</span>
                      </div>
                    </label>
                  </div>

                  <div className="permission-item">
                    <label className="permission-label">
                      <input
                        type="checkbox"
                        checked={permissions.view_mood_data}
                        onChange={() => togglePermission('view_mood_data')}
                        data-testid="permission-mood"
                      />
                      <div className="permission-info">
                        <span className="permission-name">😊 Stimmungsdaten sehen</span>
                        <span className="permission-desc">Vor/Nach Stimmungswerte deiner Sessions</span>
                      </div>
                    </label>
                  </div>

                  <div className="permission-item">
                    <label className="permission-label">
                      <input
                        type="checkbox"
                        checked={permissions.recommend_frequencies}
                        onChange={() => togglePermission('recommend_frequencies')}
                        data-testid="permission-recommend"
                      />
                      <div className="permission-info">
                        <span className="permission-name">✨ Frequenzen empfehlen</span>
                        <span className="permission-desc">Darf dir Frequenzen vorschlagen</span>
                      </div>
                    </label>
                  </div>

                  <div className="permission-item">
                    <label className="permission-label">
                      <input
                        type="checkbox"
                        checked={permissions.write_notes}
                        onChange={() => togglePermission('write_notes')}
                        data-testid="permission-notes"
                      />
                      <div className="permission-info">
                        <span className="permission-name">📝 Begleitungs-Notizen schreiben</span>
                        <span className="permission-desc">Private Notizen (nur für Therapeut sichtbar)</span>
                      </div>
                    </label>
                  </div>

                  <div className="request-actions">
                    <button
                      onClick={() => handleRespond(request.id, true)}
                      className="btn-accept"
                      disabled={loading}
                      data-testid="accept-button"
                    >
                      ✅ Akzeptieren
                    </button>
                    <button
                      onClick={() => handleRespond(request.id, false)}
                      className="btn-deny"
                      disabled={loading}
                      data-testid="deny-button"
                    >
                      ❌ Ablehnen
                    </button>
                  </div>
                </div>
              ) : (
                <div className="request-actions">
                  <button
                    onClick={() => setRespondingTo(request.id)}
                    className="btn-review"
                    data-testid="review-button"
                  >
                    Berechtigungen wählen
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PractitionerAccessDialog;
